/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import type { PreloadedQuery } from 'react-relay';
import { fg } from '@atlassian/jira-feature-gating';
import { getProjectKeyId } from '@jira/router-resources__utils/src/getProjectKeyId.tsx';
import QUERY, {
	type HorizontalNavJiraInnerQuery,
} from '@jira/navigation-apps__horizontal-nav-jira/src/ui/__generated__/HorizontalNavJiraInnerQuery.graphql';
import { createRelayResource } from '@jira/platform__graphql-relay-utils/src/services/resources/index.tsx';
import { JiraProjectAri } from '@atlassian/ari/jira/project';
import type { RouteResource } from '@atlassian/react-resource-router';
import { withGiraAggAnonSsrFix } from '../../../common/utils.tsx';

const NAVIGATION_ITEMS_PAGE_SIZE = 75;

export const getQuery: Parameters<
	typeof createRelayResource<HorizontalNavJiraInnerQuery>
>[0]['getQuery'] = ({ match, query }, { tenantContext: { cloudId } }) => {
	const boardId = match.params.boardId;
	let { projectKey, projectId } = getProjectKeyId(match, query);
	projectKey = projectKey ?? '';
	projectId = projectId ?? '';
	const projectIdOrKey = projectId || projectKey;

	const isTMPBoardRoute = boardId ? match.url.includes('/software/projects/') : false;

	const boardAri = boardId ? `ari:cloud:jira-software:${cloudId}:board/${boardId}` : '';

	const projectAri = projectId
		? JiraProjectAri.create({
				siteId: cloudId,
				projectId,
			}).toString()
		: '';

	// On TMP board routes the legacy logic gated the scopeId on projectAri (which is empty when only
	// a projectKey is available), causing a projectKeyQuery fallback even though the board ARI was
	// already known. That made the backend resolve navigation from the most recently used board
	// rather than the board the user clicked. Behind the gate we always prefer the known board ARI.
	const useLegacyTMPBoardRoute = isTMPBoardRoute && !fg('hsbc-14_jira_sidebar_nav_board_ari');

	let containerNavigationInput;
	if (useLegacyTMPBoardRoute) {
		containerNavigationInput = projectAri
			? { scopeId: boardAri }
			: { projectKeyQuery: { cloudId, projectKey } };
	} else {
		containerNavigationInput =
			boardAri || projectAri
				? { scopeId: boardAri || projectAri }
				: { projectKeyQuery: { cloudId, projectKey } };
	}

	// mostRecentlyViewedBoard is only ever rendered as a fallback for company-managed (CMP) software
	// project routes that don't have a board in the URL (the nav header discards it for TMP and
	// whenever a board ARI is known). CMP routes carry a /software/c/ segment in the URL, while TMP
	// routes use /software/projects/; only the CMP board view loses its /c prefix during URL
	// harmonization, and that route always has a board id, so it is still skipped. Behind the gate we
	// therefore only request it for boardless CMP routes. The legacy path keeps skipping based on
	// isTMPBoardRoute.
	const isCMPRoute = match.url.includes('/software/c/');
	const skipMostRecentlyViewedBoard = fg('hsbc-20_jira_nav_skip_mrv_board_when_in_url')
		? !(isCMPRoute && !boardAri)
		: isTMPBoardRoute;

	return {
		options: { fetchPolicy: 'store-and-network' },
		parameters: QUERY,
		variables: {
			cloudId,
			projectIdOrKey,
			withProject: Boolean(projectIdOrKey),
			boardId: boardAri,
			withBoard: Boolean(boardAri),
			containerNavigationInput,
			skipMostRecentlyViewedBoard,
			first: NAVIGATION_ITEMS_PAGE_SIZE,
		},
	};
};

const horizontalNavJiraResourceActual = withGiraAggAnonSsrFix(
	createRelayResource<HorizontalNavJiraInnerQuery>({
		type: 'HORIZONTAL_NAVIGATION_JIRA_QUERY',
		getQuery,
	}),
);

export const horizontalNavJiraResource: RouteResource<
	PreloadedQuery<HorizontalNavJiraInnerQuery, Record<string, unknown>>
> = horizontalNavJiraResourceActual;
